import React, { CSSProperties, useEffect, useState } from 'react'
import { Switch, Route, useLocation, useHistory } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'

// Hooks
import useUser from 'Hooks/useUser'
import useOrganization from 'Hooks/useOrganization'
import useOrganizationV2 from 'Hooks/useOrganizationV2'
import useBrand, { removeAnonymousSessionIdFromLocalStorage, saveAnonymousSessionIdToLocalStorage } from 'Hooks/useBrand'
import useScreen from 'Hooks/useScreen'
import useMaestroChat from 'Hooks/useMaestroChat'
import { useNavbar } from 'Hooks/useNavbar'
import useSignupFlow from 'Hooks/useSignupFlow'
import usePlans from 'Hooks/usePlans'

// Components - Shared routes (work in both auth states)
import AssistantPage from 'pages/AssistantPage'
import MarketingCampaignDetails from 'pages/MarketingCampaignDetails/MarketingCampaignDetails'
import NotFoundPage from 'pages/NotFoundPage'
import Navbar from 'components/Navbar/Navbar'
import Footer from 'components/Footer/Footer'

// Components - Auth-protected routes
import SettingsPage from 'pages/SettingsPage/SettingsPage'
import WebsiteHomepage from 'pages/WebsitePage/Website'
import Dashboard from 'pages/DashboardPage/DashboardPage'
import MarketingPage from 'pages/MarketingPage/MarketingPage'
import Fans from 'pages/FansPage/Fans'
import CreateNewSymphonyProfile from 'pages/AddArtist/CreateNewSymphonyProfile'
import MarketingSuccessPage from 'pages/MarketingPage/SuccessPage'
import PreSaveProvider from 'pages/MarketingPage/Data/PreSaveProvider'
import PreSave from 'pages/MarketingPage/PreSave/PreSave'
import GetPlaylisted from 'pages/MarketingPage/Playlisting'
import VideoViews from 'pages/MarketingPage/VideoAds/VideoViews'
import CreativeGenerator from 'pages/MarketingPage/CreativeGenerator'
import EnterEmail from 'pages/EnterEmail/EnterEmail'
import ReferPage from 'pages/Refer'
import AdminVideoViewsDataUpload from 'pages/Admin/VideoViewsDataUpload'
import CampaignReports from 'pages/Admin/reports'
import Learn from 'pages/LearnPage/Learn'
import KPITrends from 'pages/Admin/kpis'
import AffiliatesDashboard from 'pages/Admin/AffiliatesDashboard'
import SourcesTrends from 'pages/Admin/sources'
import TopPerforming from 'pages/Admin/TopPerforming'
import UploadEmailsByCsv from 'pages/FansPage/Components/UploadEmailsByCsv/UploadEmailsByCsv'
import AdminOnboarding from 'pages/OrganizationPage/componentsOrganization/AdminOnboarding'
import ElementsPage from 'pages/ElementsPage'
import TestApiPage from 'pages/TestApiPage'
import TiktokCallbackPage from 'pages/TikTokLoginCallback'
import CustomerSupportBrandEditingTools from 'pages/CustomerSupportBrandEditingTools'
import MessageYourFans from 'pages/FansPage/Components/MessageYourFans/MessageYourFans'
import EmailDetailsV2 from 'pages/EmailDetailsV2'
import TablePage from 'pages/TablePage'
import Clipper from 'pages/Admin/Clipper'
import AdCreatorDemo from 'components/AdCreatorDemo'
import MarketingFlow2 from 'components/MarketingFlow2'
import CampaignSubmissionView from 'components/CampaignSubmissionView'
import WebsiteBuilder from 'components/websiteBuilder/WebsiteBuilder'
import WebsitePage from 'pages/WebsitePage/components/WebsitePage'
import AllWebsites from 'pages/AllWebsites'
import AddArtistV2 from 'pages/AddArtistV2'
import AddBrandV3 from 'pages/AddBrandV3'
import OrganizationPageV2 from 'pages/OrganizationPageV2'
import ApiKeysPage from 'pages/ApiKeyPage'
import CampaignsDashboard from 'pages/CampaignsDashboard'
import Unsubscribe from 'pages/Unsubscribe'

// Providers and Modals
import UpgradeModalProvider from 'Hooks/UpgradeModalContext'
import NavbarProvider from 'pages/MarketingPage/Data/NavbarProvider'
import CreateNewCampaignModalV2 from 'components/CreateNewCampaignModalV2'
import CreateNewWebsiteModalV2 from 'components/shareable/CreateNewWebsiteModalV2'
import PaymentPastDueModal from 'components/shareable/PaymentPastDueModal'

// UI Components
import LoaderOverlay from 'elements/LoaderOverlay'

// Utils and Constants
import { NAV_BAR_HEIGHT_MOBILE, PlaybookType } from 'modules/Const'
import { styx } from 'modules/Utils'
import { CurrentBrand, OrganizationInvitationStatus, OrganizationStatus } from 'modules/Types'
import Logger from 'Logger'
import Axios from 'helpers/Interceptor'
import { OLD_SITE_URL } from 'modules/Utils'
import Colors from 'modules/Colors'

// Simple debug component to test route matching
const DebugAddArtistComponent = () => {
    return (
        <div style={{
            background: 'red',
            color: 'white',
            padding: 20,
            margin: 20,
            fontSize: 18,
            textAlign: 'center'
        }}>
            DEBUG: /add-artist route is working! AddArtistV2 should load here.
        </div>
    );
};

// External routes that redirect to old site
const externalRoutes: Record<string, string> = {
    '/about': `${OLD_SITE_URL}/about`,
    '/blog': `${OLD_SITE_URL}/blog`,
    '/case-studies': `${OLD_SITE_URL}/case-studies`,
    '/solutions/website-creation': `${OLD_SITE_URL}/solutions/website-creation`,
    '/solutions/automated-marketing': `${OLD_SITE_URL}/solutions/automated-marketing`,
    '/solutions/fan-insights': `${OLD_SITE_URL}/solutions/fan-insights`,
    '/what-is-possible/identify-and-understand-your-audience': `${OLD_SITE_URL}/what-is-possible/identify-and-understand-your-audience`,
    '/help-center': 'https://support.symphonyos.co/en/',
    '/video-walkthroughs': 'https://www.youtube.com/@Symphony-OS',
}

// Helper function to check if a route should redirect externally
const checkIsExternalRoute = (pathname: string): string | null => {
    // Check exact matches first
    if (externalRoutes[pathname]) {
        return externalRoutes[pathname]
    }

    // Check for partnership patterns
    const partnershipsPatterns = ['/partners/', '/partnerships']
    const shouldRedirectToOldSite = partnershipsPatterns.some(pattern => pathname.includes(pattern))

    if (shouldRedirectToOldSite) {
        return `${OLD_SITE_URL}${pathname}`
    }

    return null
}

// Note: Route protection is handled individually by wrapping routes with AuthProtectedRoute component

/**
 * Component that handles auth-protected routes with redirect logic
 */
const AuthProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, loginWithRedirect } = useAuth0()
    const location = useLocation()

    // Check for token in URL and save it before redirect
    React.useEffect(() => {
        if (!isAuthenticated) {
            const params = new URLSearchParams(location.search)
            const token = params.get('token')

            if (token) {
                saveAnonymousSessionIdToLocalStorage(token)
                console.log('📧 Saved email campaign token to anonymous-session-id:', token)
            }
        }
    }, [isAuthenticated, location.search])

    if (!isAuthenticated) {
        loginWithRedirect({
            appState: {
                returnTo: location.pathname + location.search
            }
        })
        return (
            <LoaderOverlay
                className="loaderAuthRedirect"
                title=""
            >
                <div
                    className="redirectingToLogin"
                    style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: 20,
                        marginTop: 10,
                    }}
                >
                    Redirecting to login...
                </div>
            </LoaderOverlay>
        )
    }

    return <>{children}</>
}

/**
 * Component that initializes user session data for authenticated users
 */
const InitializationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const history = useHistory()
    const {
        isAuthenticated: isAuthenticatedWithAuth0,
        isLoading: isAuth0Loading,
        logout,
        getAccessTokenSilently,
        user: userAuth0,
    } = useAuth0()

    const { cssCalc } = useScreen()
    const {
        currentUser,
        getCurrentUser,
    } = useUser()

    const {
        currentBrand,
        getBrands,
        loadedBrands,
    } = useBrand()

    const {
        getOrganization: getOrganization_OLD,
    } = useOrganization()

    const {
        organization,
        isOrganizationInitialized,
        getOrganization: getOrganizationV2,
        setIsOrganizationInitialized
    } = useOrganizationV2()

    const { handlePostAuthFlow } = useSignupFlow()

    const { fetchPlans } = usePlans()

    /** Initializes the user session with Auth0 and database details */
    const initializeUserSession = async () => {
        try {
            // Wait for Auth0 to finish loading before making any decisions
            if (isAuth0Loading) {
                return
            }

            // This component should only be rendered for authenticated users
            if (!isAuthenticatedWithAuth0) {
                console.error('InitializationProvider rendered for unauthenticated user')
                return
            }

            // Add guards against re-initialization
            const userSubAuth0 = userAuth0?.sub
            if (!userSubAuth0) {
                return
            }

            // Handle TikTok auth code if present
            const params = new URLSearchParams(window.location.search)
            const authCode = params.get('auth_code')
            if (authCode) {
                localStorage.setItem('tt_auth_code', authCode)
            }

            const currentUserLocal = await getCurrentUser(
                true,
                userSubAuth0,
                getAccessTokenSilently,
                logout,
            )

            const currentBrandLocal = await getBrands(true, true) as CurrentBrand
            if (!currentBrandLocal?.id) {
                // Check for post-authentication flow (anonymous brand linking, temporary brand migration, etc.)
                await handlePostAuthFlow(currentUserLocal, {
                    history
                })

            }
            // Only initialize organization if user has a brand
            if (currentBrandLocal?.id) {
                // Fetch plans once for the app session using the brand's currency
                await fetchPlans({
                    currency: currentBrandLocal.currency?.code || 'USD',
                    useCache: false
                })

                const organizationV2Data = await getOrganizationV2({
                    brandId: currentBrandLocal.id,
                    userId: currentUserLocal?.id,
                })
                const organizationId = organizationV2Data?.organization.id
                if (organizationV2Data && organizationId) {
                    getOrganization_OLD(organizationId)
                    const organizationUsersLocal = organizationV2Data?.organizationUsers
                    const invitationStatusLocal = organizationUsersLocal?.find(ou => ou.user_id === currentUserLocal?.id)?.invitation_status

                    if (invitationStatusLocal === OrganizationInvitationStatus.PENDING) {
                        history.push('/team')
                    }
                }
            } else {
                // For new users without brands, manually set organization as initialized
                // to prevent the loading overlay from persisting
                const { setOrganization } = useOrganizationV2.getState()
                setOrganization(undefined)
                setIsOrganizationInitialized(true)
            }
        } catch (err) {
            Logger.error('Error initializing session:', err)
            // For authenticated users, if there's an error, we should logout
            logout()
        }
    }

    useEffect(() => {
        if (!isAuth0Loading) {
            initializeUserSession()
        }
    }, [isAuth0Loading, isAuthenticatedWithAuth0, logout])

    useEffect(() => {
        if (!currentUser || !loadedBrands || !isOrganizationInitialized) {
            return // Wait for full initialization before any redirects
        }

        if (!currentUser.email) {
            history.push('/email')
            return
        }

        // Check for signup flow context that should skip add-artist redirect
        const hasPreAuthData = localStorage.getItem('pre-auth-chat-id') ||
            localStorage.getItem('pre-auth-campaign-id') ||
            localStorage.getItem('anonymous-session-id') // Use anonymous session ID directly

        if (!loadedBrands.length && !hasPreAuthData) {
            history.push('/add-artist')
            return
        }

        if (organization?.setup_status === OrganizationStatus.WAITING_FOR_DETAILS) {
            history.push('/team')
            return
        }
    }, [currentUser, loadedBrands, organization, isOrganizationInitialized])

    // Show loading until initialization is complete
    if (!currentUser || !loadedBrands || !isOrganizationInitialized) {
        return (
            <LoaderOverlay
                className="loaderFullScreenInitializeApp"
                title=""
            >
                <div
                    className="welcomeToSymphony"
                    style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: 20,
                        marginTop: 10,
                    }}
                >
                    Welcome to Symphony
                </div>
            </LoaderOverlay>
        )
    }

    return <div
        className="initializeAppComponent"
        style={styx(
            sty.initializeAppComponent,
            {
                height: cssCalc()
            }
        )}
    >{children}</div>
}

/**
 * Main App component that handles all routing and authentication states
 */
const MainAppRouter = () => {
    const location = useLocation()
    const { isAuthenticated, isLoading } = useAuth0()
    const { mStyle, mobileView, cssCalc } = useScreen()
    const {
        isMobileNavbarShown,
        isWebsiteChildPage
    } = useNavbar()
    const { currentBrand } = useBrand()
    const { organization } = useOrganization()
    const { isChatOpen } = useMaestroChat()


    // Handle external route redirects
    useEffect(() => {
        const { pathname, search } = location
        const externalUrl = checkIsExternalRoute(pathname)

        if (externalUrl) {
            // Add search params if they exist
            const fullUrl = search ? `${externalUrl}${search}` : externalUrl
            window.location.href = fullUrl
        }
    }, [location])

    // Show loading during Auth0 initialization
    // if (isLoading) {
    //     return null
    // }

    // Layout calculations (from PostAuthRoutes)
    let mainAppWidth = '100%'
    let overflowX = 'hidden'

    const getMainAppHeight = () => {
        // In chat mode on mobile, use full viewport height
        if (mobileView && isChatOpen) {
            return '100%'
        }

        if (mobileView && isMobileNavbarShown()) {
            let heightOffset = NAV_BAR_HEIGHT_MOBILE
            return cssCalc(heightOffset)
        }

        return cssCalc()
    }

    if (isWebsiteChildPage()) {
        overflowX = 'visible'
    }

    // Payment modals are handled via PaymentPastDueModal with autoShow={true}

    const getFlexDirection = () => {
        if (isAuthenticated) {
            return 'row'
        }
        if (location.pathname.includes('/adbox/campaigns/')) {
            return 'row'
        }
        return 'column'
    }


    const renderContent = () => {
        return (
            <div
                className="mainAppContent fade-in"
                style={styx(
                    sty.mainAppContent,
                    { flexDirection: getFlexDirection() }
                )}
            >
                <Navbar className="navbarFromMainApp" />

                <div
                    className="mainAppRoutesContainer"
                    style={styx(
                        mStyle(sty, 'mainAppRoutesContainer'),
                        { height: getMainAppHeight() },
                        { width: mainAppWidth },
                        { overflowX: overflowX as CSSProperties['overflowX'] },
                    )}
                >
                    <Switch>
                        {/* Routes that work in both authenticated and unauthenticated states */}
                        <Route exact path={['/', '/assistant', '/chats/:chatId']}>
                            <AssistantPage isPublic={!isAuthenticated} />
                        </Route>

                        <Route exact path="/adbox/campaigns/:campaignId">
                            <MarketingCampaignDetails
                                className={isAuthenticated ? "marketingCampaignDetailsComponentPostAuth" : "marketingCampaignDetailsComponentPublic"}
                                style={{ position: 'relative' }}
                                isPublic={!isAuthenticated}
                            />
                        </Route>

                        <Route exact path="/unsubscribe">
                            <Unsubscribe />
                        </Route>
                        {/* Auth-protected routes */}
                        <Route exact path="/setup">
                            <AuthProtectedRoute>
                                <AddBrandV3 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing2/new/:playbookType">
                            <AuthProtectedRoute>
                                <MarketingFlow2 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing2/draft/:draftId">
                            <AuthProtectedRoute>
                                <MarketingFlow2 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing2/submission/:campaignId">
                            <AuthProtectedRoute>
                                <CampaignSubmissionView />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/email">
                            <AuthProtectedRoute>
                                <EnterEmail />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing" >
                            <AuthProtectedRoute>
                                <MarketingPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/dashboard">
                            <AuthProtectedRoute>
                                <Dashboard />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/campaigns/:campaignId">
                            <AuthProtectedRoute>
                                <MarketingCampaignDetails className="marketingCampaignDetailsComponentPostAuth" />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/creatives">
                            <AuthProtectedRoute>
                                <CreativeGenerator />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/presave">
                            <AuthProtectedRoute>
                                <PreSaveProvider>
                                    <PreSave setGoal={() => null} />
                                </PreSaveProvider>
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/music-streams">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.INCREASE_MUSIC_STREAMS} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/grow-playlist-followers">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.GROW_PLAYLIST_FOLLOWERS} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/grow-instagram-engagement">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/playlists">
                            <AuthProtectedRoute>
                                <GetPlaylisted />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/video-views">
                            <AuthProtectedRoute>
                                <VideoViews />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/tiktok-ads">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.TIKTOK_BOOST_ENGAGEMENT} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/marketing/new/link-clicks">
                            <AuthProtectedRoute>
                                <MarketingFlow2 playbookType={PlaybookType.INCREASE_LINK_CLICKS} />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/add-artist-OLD">
                            <AuthProtectedRoute>
                                <CreateNewSymphonyProfile />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/add-artist">
                            <AuthProtectedRoute>
                                <AddArtistV2 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/goals">
                            <AuthProtectedRoute>
                                <DebugAddArtistComponent />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/marketing/success">
                            <AuthProtectedRoute>
                                <MarketingSuccessPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/tiktok/callback">
                            <AuthProtectedRoute>
                                <TiktokCallbackPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/website">
                            <AuthProtectedRoute>
                                <WebsitePage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/website-builder">
                            <AuthProtectedRoute>
                                <WebsiteBuilder />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/website-builder/:brandSlug/:websiteSlug">
                            <AuthProtectedRoute>
                                <WebsiteBuilder />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/fans/campaigns/:id">
                            <AuthProtectedRoute>
                                <EmailDetailsV2 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/fans">
                            <AuthProtectedRoute>
                                <Fans />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/import-fans">
                            <AuthProtectedRoute>
                                <UploadEmailsByCsv />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/message-your-fans">
                            <AuthProtectedRoute>
                                <MessageYourFans />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/website/:websiteType/:websiteSlug">
                            <AuthProtectedRoute>
                                <WebsiteHomepage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/elementspage">
                            <AuthProtectedRoute>
                                <ElementsPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/testapi">
                            <AuthProtectedRoute>
                                <TestApiPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admintools">
                            <AuthProtectedRoute>
                                <CustomerSupportBrandEditingTools />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/allwebsites">
                            <AuthProtectedRoute>
                                <AllWebsites />
                            </AuthProtectedRoute>
                        </Route>

                        <Route path="/learn">
                            <AuthProtectedRoute>
                                <Learn />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/settings">
                            <AuthProtectedRoute>
                                <SettingsPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/team">
                            <AuthProtectedRoute>
                                <OrganizationPageV2 />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/onboarding">
                            <AuthProtectedRoute>
                                <AdminOnboarding />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/refer">
                            <AuthProtectedRoute>
                                <ReferPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/video-views-data-upload">
                            <AuthProtectedRoute>
                                <AdminVideoViewsDataUpload />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/reports">
                            <AuthProtectedRoute>
                                <CampaignReports />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/top-performing">
                            <AuthProtectedRoute>
                                <TopPerforming />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/kpis">
                            <AuthProtectedRoute>
                                <KPITrends />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/sources">
                            <AuthProtectedRoute>
                                <SourcesTrends />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/admin/affiliates">
                            <AuthProtectedRoute>
                                <AffiliatesDashboard />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/tools/clipper">
                            <AuthProtectedRoute>
                                <Clipper />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/tools/ad-creator">
                            <AuthProtectedRoute>
                                <AdCreatorDemo />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/table">
                            <AuthProtectedRoute>
                                <TablePage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/api-keys">
                            <AuthProtectedRoute>
                                <ApiKeysPage />
                            </AuthProtectedRoute>
                        </Route>

                        <Route exact path="/campaigns-dashboard">
                            <AuthProtectedRoute>
                                <CampaignsDashboard />
                            </AuthProtectedRoute>
                        </Route>

                        {/* Catch-all route */}
                        <Route path="*">
                            <NotFoundPage isPublic={!isAuthenticated} />
                        </Route>
                    </Switch>

                    {/* Footer for public routes when chat is not open */}
                    {!isAuthenticated && !isChatOpen && (
                        <Footer className="mainAppFooter" />
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="mainApp" style={styx(sty.mainApp)}>
            {/* UpgradeModalProvider wraps everything so it's available for both auth states */}
            <UpgradeModalProvider>
                {isAuthenticated ? (
                    // Authenticated users get full initialization and providers
                    <InitializationProvider>
                        <NavbarProvider>
                            {renderContent()}

                            {/* Auth-dependent modals */}
                            <PaymentPastDueModal autoShow={true} />

                            {currentBrand?.id && (
                                <>
                                    <CreateNewCampaignModalV2
                                        className="createNewCampaignModalMainApp"
                                    />
                                    <CreateNewWebsiteModalV2
                                        className="createNewWebsiteModalMainApp"
                                    />
                                </>
                            )}
                        </NavbarProvider>
                    </InitializationProvider>
                ) : (
                    // Unauthenticated users get minimal providers
                    renderContent()
                )}
            </UpgradeModalProvider>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    mainApp: {
        display: 'flex',
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#181818',
        backgroundImage: 'url(https://cdn.prod.website-files.com/61fc721130a4089acadae508/6685b3d152d239f14cf16fb7_bg.svg)',
        backgroundPosition: '50%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'auto',
        backgroundAttachment: 'fixed',
    },
    mainAppContent: {
        display: 'flex',
        width: '100%',
        flex: 1,
    },
    mainAppRoutesContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginLeft: 'auto',
        flex: 1,
        backgroundColor: Colors.greyLighter
    },
    mainAppRoutesContainer_mobile: {
        marginLeft: 0,
    },
    initializeAppComponent: {
        display: "flex",
        width: "100%",
    }
}


export default MainAppRouter 